import { wealthProApi } from "../../redux/api";
import { useCacheKeyMergeHandler, useCacheKeySerializeQueryArgsHandler, useCacheKeyTransformResponseHandler } from "../../redux/helpers";

const getSearchParams = (object) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(object)) {
        if (value != null && value !== "")
            params.append(key, value);
    }
    return params.toString();
}

const clientSetManagementApiEndpoints = wealthProApi.enhanceEndpoints({
    addTagTypes: ["clientSetDetails", "clientSetList"]
}).injectEndpoints({
    endpoints: (builder) => ({
        fetchClientSetsList: builder.query({
            query: () => `api/campaignmanagement/client-sets/list`,
            providesTags: ["clientSetList"]
        }),
        fetchClientSetDetails: builder.query({
            query: ({ clientSetId }) => `api/campaignmanagement/client-sets/${clientSetId}`,
            providesTags: (res, err, { clientSetId }) => res && !err && [{ type: 'clientSetDetails', id: clientSetId }],
        }),
        fetchClientSetMasterAccounts: builder.query({
            query: ({ clientSetId, ...queryParams }) => `api/campaignmanagement/client-sets/${clientSetId}/master-accounts?${getSearchParams(queryParams)}`,
            serializeQueryArgs: useCacheKeySerializeQueryArgsHandler,
            merge: useCacheKeyMergeHandler,
            transformResponse: useCacheKeyTransformResponseHandler
        }),
        createClientSet: builder.mutation({
            query: (body) => ({
                url: `api/campaignmanagement/client-sets`,
                method: "POST",
                body
            }),
            invalidatesTags: ["clientSetList"]
        }),
        joinClientSets: builder.mutation({
            query: ({ clientSetId, ...req }) => ({
                url: `api/campaignmanagement/client-sets/${clientSetId}/join`,
                method: "PATCH",
                body: req
            }),
            invalidatesTags: (_res, _err, { clientSetId }) => ["clientSetList", { type: 'clientSetDetails', id: clientSetId }]
        }),
        editClientSetMasterAccounts: builder.mutation({
            query: ({ clientSetId, ...req }) => ({
                url: `api/campaignmanagement/client-sets/${clientSetId}/master-accounts`,
                method: "PATCH",
                body: req
            }),
            invalidatesTags: (_res, _err, { clientSetId }) => ["clientSetList", { type: 'clientSetDetails', id: clientSetId }]
        }),
        deleteClientSet: builder.mutation({
            query: ({ clientSetId }) => ({
                url: `api/campaignmanagement/client-sets/${clientSetId}`,
                method: "DELETE"
            }),
            invalidatesTags: ["clientSetList"]
        }),
        createClientSetProject: builder.mutation({
            query: ({ clientSetId, ...project }) => ({
                url: `api/campaignmanagement/client-sets/${clientSetId}/projects`,
                method: "POST",
                body: project
            })
        }),
        exportClientSetContactDetails: builder.query({
            query: ({ clientSetId, isEmailExport, ...req }) => ({
                url: `api/campaignmanagement/client-sets/${clientSetId}/${isEmailExport ? "emails" : "contacts"}`,
                method: "POST",
                body: req,
                responseHandler: async (response) => {
                    const blob = await response.blob();
                    const disposition = response.headers.get('content-disposition');
                    let filename = "ClientSetContacts.xls";

                    const fileNameStarRegex = /(?<=filename\*=UTF-8'')([^;]+)/i;
                    const matchesStar = disposition.match(fileNameStarRegex);

                    if (matchesStar && matchesStar[1]) {
                        filename = decodeURIComponent(matchesStar[1].trim());
                    } else {
                        console.error("Cannot extract filename");
                    }

                    return { blob, filename };
                },
                cache: 'no-cache'
            }),
        })
    })
});

export const {
    useFetchClientSetsListQuery,
    useFetchClientSetDetailsQuery,
    useLazyFetchClientSetMasterAccountsQuery,
    useCreateClientSetMutation,
    useJoinClientSetsMutation,
    useEditClientSetMasterAccountsMutation,
    useDeleteClientSetMutation,
    useCreateClientSetProjectMutation,
    useLazyExportClientSetContactDetailsQuery
} = clientSetManagementApiEndpoints;